import { Fragment, ReactNode, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Tooltip, TooltipTrigger } from '@design-system';
import { CarePhase } from '@shared/appModel';
import {
  DAYS_IN_MONTH,
  ONE_ADL_DURATION_MULTIPLIER,
} from '@shared/calculations/funding/projection/sharedConstants';
import { WhenTooltipContent } from './cells';
import { ClaimantBadge } from './ClaimantBadge';
import { currency, monthRowKey, resolveMonthDisplay } from './formatters';
import { milestoneBadgeClass } from './milestoneColors';
import { ReasonText } from './ReasonText';
import {
  BreakdownLine,
  PreSegment,
  ReasonKey,
  ReasonLabel,
  breakdownLines,
  buildPreSegments,
  reasonForMonth,
} from './segments';
import { getCumulativeIneligibleCostSeries } from '@shared/calculations/funding/projection/projectionTotals';

interface PrePayoutTableProps {
  months: MonthlyEntry[];
  initialState: MonthlyState;
  terms?: ClaimTerms;
  // Per-claimant relative-month map (joint policies). Computed from the
  // full projection by the parent so the count is continuous across the
  // pre-payout / payout split. Omit for single-life.
  monthDisplayMap?: Map<string, number>;
}

/**
 * Summary view of the eligibility gap (months before any payout). Groups
 * consecutive same-reason months into one collapsible segment row. Each row
 * stacks every out-of-pocket reason its months carry with a day count, so a
 * transition month that begins under the 2-ADL gate and ends serving WP is
 * surfaced honestly instead of hidden behind its primary reason.
 *
 * Single-column header (no group row), four columns:
 *   When · Projected Cost · Cumulative Out of Pocket · Reason
 */
export function PrePayoutTable({
  months,
  initialState,
  terms,
  monthDisplayMap,
}: PrePayoutTableProps) {
  const segments = useMemo(
    () => buildPreSegments(months, initialState),
    [months, initialState],
  );
  const cumIneligibleSeries = useMemo(
    () => getCumulativeIneligibleCostSeries(months),
    [months],
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div>
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full border-collapse text-xs">
          <thead className="sticky top-0 z-20 bg-white shadow-[0_1px_0_rgba(0,0,0,0.08)]">
            <tr>
              <th className="sticky left-0 z-10 bg-white px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-700 shadow-[1px_0_0_rgba(0,0,0,0.06)]">
                Month of LTC
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                Care cost<sup className="ml-0.5 font-normal text-gray-500">*</sup>
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                Cumulative Out of Pocket
              </th>
              <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                Reason
              </th>
            </tr>
          </thead>
          <tbody>
            {segments.map(segment => {
              const isExpanded = expanded.has(segment.id);
              const isMulti = segment.months.length > 1;
              return (
                <Fragment key={segment.id}>
                  <SegmentSummaryRow
                    segment={segment}
                    expanded={isExpanded}
                    onToggle={isMulti ? () => toggle(segment.id) : undefined}
                    terms={terms}
                    monthDisplayMap={monthDisplayMap}
                  />
                  {isExpanded &&
                    isMulti &&
                    segment.months.map(month => {
                      const globalIdx = months.indexOf(month);
                      return (
                        <PreDetailRow
                          key={monthRowKey(month)}
                          month={month}
                          cumulativeIneligible={cumIneligibleSeries[globalIdx]}
                          terms={terms}
                          monthDisplayMap={monthDisplayMap}
                        />
                      );
                    })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SegmentSummaryRow({
  segment,
  expanded,
  onToggle,
  terms,
  monthDisplayMap,
}: {
  segment: PreSegment;
  expanded: boolean;
  onToggle?: () => void;
  terms?: ClaimTerms;
  monthDisplayMap?: Map<string, number>;
}) {
  const isMulti = segment.months.length > 1;
  // Segments are split by claimant (see `buildPreSegments` / docs A10), so
  // every month inside a segment shares one claimant and the relative-month
  // numbering is monotonic across the range.
  const segmentClaimant = segment.months[0].context.claimant;
  const start = resolveMonthDisplay(segment.startMonthIndex, segmentClaimant, monthDisplayMap);
  const end = resolveMonthDisplay(segment.endMonthIndex, segmentClaimant, monthDisplayMap);
  const whenLabel = isMulti ? `Months ${start}–${end}` : `Month ${start}`;
  const firstMonth = segment.months[0];
  const lastMonth = segment.months[segment.months.length - 1];
  const costLabel =
    segment.minCost === segment.maxCost
      ? `${currency(segment.minCost)}/mo`
      : `${currency(segment.minCost)} – ${currency(segment.maxCost)}/mo`;

  const allMilestones = segment.months.flatMap(m => m.events.milestones);

  return (
    <tr
      className={`border-b border-gray-100 ${
        onToggle ? 'cursor-pointer hover:bg-gray-50' : ''
      }`}
      onClick={onToggle}
    >
      <td className="sticky left-0 z-[1] bg-white px-3 py-2.5 shadow-[1px_0_0_rgba(0,0,0,0.06)]">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            {onToggle && (
              <ChevronRight
                className={`h-3.5 w-3.5 text-gray-700 transition-transform ${
                  expanded ? 'rotate-90' : ''
                }`}
              />
            )}
            <ClaimantBadge claimant={firstMonth.context.claimant} />
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm font-semibold text-gray-900 cursor-help">
                  {whenLabel}
                </span>
              </TooltipTrigger>
              <WhenTooltipContent startMonth={firstMonth} endMonth={lastMonth} terms={terms} />
            </Tooltip>
            {isMulti && (
              <span className="text-[11px] text-gray-500">({segment.months.length} mo)</span>
            )}
          </div>
          {allMilestones.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {allMilestones.map((m, mi) => (
                <span
                  key={mi}
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap ${milestoneBadgeClass(m.kind)}`}
                >
                  {m.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right text-xs tabular-nums text-gray-600">{costLabel}</td>
      <td className="px-3 py-2.5 text-right text-base font-bold tabular-nums text-rose-700">
        {currency(segment.endCumulativeIneligible)}
      </td>
      <td className="px-3 py-2.5 text-left">
        <ReasonBreakdown
          lines={breakdownLines(segment.breakdown)}
          fallback={segment.reason}
          variant="summary"
          tooltips={buildReasonTooltips(firstMonth, terms)}
        />
      </td>
    </tr>
  );
}

function PreDetailRow({
  month,
  cumulativeIneligible,
  terms,
  monthDisplayMap,
}: {
  month: MonthlyEntry;
  cumulativeIneligible: number;
  terms?: ClaimTerms;
  monthDisplayMap?: Map<string, number>;
}) {
  const reason = reasonForMonth(month);
  return (
    <tr className="border-b border-gray-100 bg-gray-50/40">
      <td className="sticky left-0 z-[1] bg-gray-50/40 px-3 py-1.5 pl-8 shadow-[1px_0_0_rgba(0,0,0,0.06)]">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-xs text-gray-700 cursor-help">
              Month {resolveMonthDisplay(month.context.monthIndex, month.context.claimant, monthDisplayMap)}
            </span>
          </TooltipTrigger>
          <WhenTooltipContent startMonth={month} terms={terms} />
        </Tooltip>
      </td>
      <td className="px-3 py-1.5 text-right text-xs tabular-nums text-gray-500">
        {currency(month.facts.effectiveCareCost)}
      </td>
      <td className="px-3 py-1.5 text-right text-xs tabular-nums text-rose-700">
        {currency(cumulativeIneligible)}
      </td>
      <td className="px-3 py-1.5 text-left">
        <ReasonBreakdown
          lines={breakdownLines(month.events.ineligibleDayBreakdown)}
          fallback={reason}
          variant="detail"
          tooltips={buildReasonTooltips(month, terms)}
        />
      </td>
    </tr>
  );
}

/**
 * Renders the per-reason day breakdown as a stacked list — one row per
 * reason with `Nd` to the right. Falls back to the primary reason label
 * with no day count when the breakdown is empty (rare: a non-paying row
 * with no classified ineligible days, e.g. a CM II partial-month strand).
 *
 * `tooltips` provides per-key rich tooltip overrides (e.g. the <2 ADL
 * derivation tooltip). Reasons without an override fall back to the
 * static `REASON_TOOLTIPS` string inside `ReasonText`.
 */
function ReasonBreakdown({
  lines,
  fallback,
  variant,
  tooltips,
}: {
  lines: BreakdownLine[];
  fallback: ReasonLabel;
  variant: 'summary' | 'detail';
  tooltips?: Partial<Record<ReasonKey, ReactNode>>;
}) {
  const reasonClassName = variant === 'detail' ? 'text-xs text-gray-600' : undefined;
  const dayClassName =
    variant === 'detail'
      ? 'ml-2 text-[10px] tabular-nums text-gray-400'
      : 'ml-2 text-[11px] tabular-nums text-gray-500';

  if (lines.length === 0) {
    return (
      <ReasonText
        reason={fallback}
        className={reasonClassName}
        tooltipContent={tooltips?.[fallback.key]}
      />
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {lines.map((line, i) => (
        <ReasonText
          key={`${line.reason.key}-${i}`}
          reason={line.reason}
          className={reasonClassName}
          trailing={<span className={dayClassName}>· {line.days}d</span>}
          tooltipContent={tooltips?.[line.reason.key]}
        />
      ))}
    </div>
  );
}

/**
 * Tooltip body for the "Less than 2 ADLs active" badge — shows the rule plus
 * the math derivation for this client's early-care phase. The bucket size is
 * `roundedPhaseMonths × DAYS_IN_MONTH × ONE_ADL_DURATION_MULTIPLIER`, the
 * same formula `computeNon2AdlDaysThisRow` uses in the calc — kept here only
 * for display, never re-derived for math.
 */
function Lt2AdlTooltipContent({ phaseCareMonths }: { phaseCareMonths: number }) {
  const roundedPhaseMonths = Math.round(phaseCareMonths);
  const earlyCareDays = roundedPhaseMonths * DAYS_IN_MONTH;
  const bucketDays = earlyCareDays * ONE_ADL_DURATION_MULTIPLIER;
  const bucketMonths = bucketDays / DAYS_IN_MONTH;
  return (
    <>
      <div className="mb-1 font-semibold">Less than 2 ADLs active</div>
      <div className="mb-2 text-[10px] text-gray-300">
        The policy treats the first half of the early-care phase as the
        &lt;2&nbsp;ADL ineligibility bucket — until the client needs ≥2 ADLs
        of assistance, benefits don't engage.
      </div>
      <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-400">
        Bucket size — this phase
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-[11px] tabular-nums">
        <span className="text-gray-300">Early-care duration</span>
        <span className="text-right font-semibold">{phaseCareMonths.toFixed(1)} mo</span>
        <span className="text-gray-300">× ½ (phase fraction)</span>
        <span className="text-right">× {ONE_ADL_DURATION_MULTIPLIER}</span>
        <span className="text-gray-300">× days per month</span>
        <span className="text-right">× {DAYS_IN_MONTH}</span>
        <span className="col-span-2 my-1 border-t border-gray-600" aria-hidden />
        <span className="font-semibold">= &lt;2 ADL bucket</span>
        <span className="text-right font-semibold">
          {bucketDays} d
          <span className="ml-1 font-normal text-gray-400">
            ({bucketMonths.toFixed(1)} mo)
          </span>
        </span>
      </div>
    </>
  );
}

/**
 * Tooltip body for the "Before policy start" badge. Before the policy's
 * premium-start year the policy doesn't exist, so the whole month is
 * out-of-pocket no matter the care details — these months are grouped under a
 * single "before policy start" reason rather than split by the policy's
 * eligibility gates. The <2 ADL window (always half the early-care phase) and
 * the elimination period still exist on the care timeline; they just don't
 * change anything until the policy is in force and can actually pay.
 *
 * `careStartYear` is the onset on the SAME policy-year clock that drove the
 * pre-policy classification (premiumStart + policyYear − 1), so the date rows
 * are self-consistent with the badge — see PrePayoutTable's call site.
 */
function PrePolicyTooltipContent({
  careStartYear,
  policyPremiumStartYear,
}: {
  careStartYear: number;
  policyPremiumStartYear: number;
}) {
  return (
    <>
      <div className="mb-1 font-semibold">Before policy start</div>
      <div className="mb-2 text-[10px] text-gray-300">
        This care is projected to begin before the policy&apos;s premium-start
        year, so the policy does not yet exist and can&apos;t pay — the full cost
        is out of pocket regardless of the care details. The &lt;2&nbsp;ADL
        window and elimination (waiting) period only affect what the policy pays
        once it&apos;s in force, so these months aren&apos;t split by those
        reasons.
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 text-[11px] tabular-nums">
        <span className="text-gray-300">Care predicted to start</span>
        <span className="text-right font-semibold">{careStartYear}</span>
        <span className="text-gray-300">Policy begins</span>
        <span className="text-right font-semibold">{policyPremiumStartYear}</span>
      </div>
    </>
  );
}

/**
 * Resolves per-reason rich-tooltip overrides for a single row's data. Pulls
 * the row's carePhase off the entry and the matching phaseCareMonths off
 * terms — both are needed to derive the per-phase bucket size shown in the
 * <2 ADL tooltip. Returns an empty map when terms aren't available or the
 * reason isn't one we have a rich tooltip for, so `ReasonText` falls back
 * to the static REASON_TOOLTIPS string.
 */
function buildReasonTooltips(
  month: MonthlyEntry,
  terms: ClaimTerms | undefined,
): Partial<Record<ReasonKey, ReactNode>> {
  if (!terms) return {};
  const tooltips: Partial<Record<ReasonKey, ReactNode>> = {};
  const carePhase = month.context.carePhase;
  const phaseMonths = terms.phaseCareMonthsByPhase?.[carePhase];
  // The 2-ADL gate only fires in earlyCare (see sharedEligibility), so the
  // derivation tooltip only makes sense there. Missing phase data → fall
  // back to the static string.
  if (carePhase === CarePhase.earlyCare && phaseMonths != null) {
    tooltips['lt-2-adl'] = <Lt2AdlTooltipContent phaseCareMonths={phaseMonths} />;
  }
  // Pre-policy rows: show the care-start vs. premium-start years that make the
  // month pre-policy. Derive the care-start year from the SAME policy-year clock
  // that drives the classification (premiumStart + policyYear − 1), NOT from
  // `facts.projectedCareYear` — the two can disagree when a partner's onset is
  // resolved from an age fallback for the clock but a "today" fallback for the
  // cost projection, which would print a self-contradictory year here.
  const careStartYear = terms.policyPremiumStartYear + month.context.policyYear - 1;
  tooltips['pre-policy'] = (
    <PrePolicyTooltipContent
      careStartYear={careStartYear}
      policyPremiumStartYear={terms.policyPremiumStartYear}
    />
  );
  return tooltips;
}
